/**
 * BrainInternalKnowledgeTab
 * =========================
 * Internal Knowledge entries table with category filters, search, CRUD modals, and history.
 * Same structure as BrainKBEntriesTab with IK-specific fields.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../hooks';
import { Alert, Button } from '../ui';
import {
  getIKCategories,
  getIKEntries,
  createIKEntry,
  updateIKEntry,
  deleteIKEntry,
} from '../../api/internalKnowledge';
import IKEntryHistoryModal from './IKEntryHistoryModal';

const CONFIDENCE_OPTIONS = ['low', 'medium', 'high'];
const WORKER_TAG_OPTIONS = ['origination', 'regulatory_structuring', 'technology', 'distribution'];

const CONFIDENCE_COLORS = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-red-100 text-red-700',
};

const emptyDecisionPoint = () => ({ point: '', context: '', rationale: '', section_source: '' });

const BrainInternalKnowledgeTab = () => {
  const { showToast } = useToast();
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [historyEntry, setHistoryEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  const [formData, setFormData] = useState({
    category: '',
    title: '',
    decision_points: [emptyDecisionPoint()],
    confidence: 'medium',
    worker_tags: [],
  });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getIKCategories();
      setCategories(res.data?.categories || []);
    } catch { showToast('Failed to load categories', 'error'); }
  }, [showToast]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await getIKEntries(null);
      setEntries(res.data?.entries || []);
    } catch (err) { setFetchError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const filteredEntries = searchQuery
    ? entries.filter(e =>
        e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : entries;

  const groupedEntries = filteredEntries.reduce((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {});

  const sortedCategoryKeys = Object.keys(groupedEntries).sort();

  useEffect(() => {
    if (searchQuery) {
      const matching = {};
      sortedCategoryKeys.forEach(cat => { matching[cat] = true; });
      setExpandedCategories(matching);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const resetForm = () => {
    setFormData({
      category: '',
      title: '',
      decision_points: [emptyDecisionPoint()],
      confidence: 'medium',
      worker_tags: [],
    });
  };

  const handleCreate = async () => {
    try {
      const dps = formData.decision_points.filter(dp => dp.point.trim());
      await createIKEntry({
        category: formData.category,
        title: formData.title,
        decision_points: dps,
        confidence: formData.confidence,
        worker_tags: formData.worker_tags,
      });
      showToast('Entry created', 'success');
      setShowCreateModal(false);
      resetForm();
      fetchEntries();
      fetchCategories();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed to create entry', 'error'); }
  };

  const handleUpdate = async () => {
    if (!editingEntry) return;
    try {
      const dps = formData.decision_points.filter(dp => dp.point.trim());
      await updateIKEntry(editingEntry.id, {
        title: formData.title,
        category: formData.category,
        decision_points: dps,
        confidence: formData.confidence,
        worker_tags: formData.worker_tags,
      });
      showToast('Entry updated', 'success');
      setEditingEntry(null);
      resetForm();
      fetchEntries();
      fetchCategories();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed to update entry', 'error'); }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Deactivate this entry?')) return;
    try {
      await deleteIKEntry(entryId);
      showToast('Entry deactivated', 'success');
      fetchEntries();
      fetchCategories();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed to delete entry', 'error'); }
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      title: entry.title,
      decision_points: entry.decision_points?.length > 0
        ? entry.decision_points.map(dp => ({ ...dp }))
        : [emptyDecisionPoint()],
      confidence: entry.confidence || 'medium',
      worker_tags: entry.worker_tags || [],
    });
  };

  const toggleWorkerTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      worker_tags: prev.worker_tags.includes(tag)
        ? prev.worker_tags.filter(t => t !== tag)
        : [...prev.worker_tags, tag]
    }));
  };

  const updateDP = (index, field, value) => {
    const updated = [...formData.decision_points];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, decision_points: updated });
  };

  const addDP = () => {
    setFormData({ ...formData, decision_points: [...formData.decision_points, emptyDecisionPoint()] });
  };

  const removeDP = (index) => {
    const updated = formData.decision_points.filter((_, i) => i !== index);
    setFormData({ ...formData, decision_points: updated.length ? updated : [emptyDecisionPoint()] });
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="relative w-full sm:w-auto">
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-64 focus:ring-0 focus:border-primary transition-all"
            />
          </div>
          <span className="text-sm text-gray-500">
            {sortedCategoryKeys.length} categories, {filteredEntries.length} entries
          </span>
        </div>
        <button
          onClick={() => { setShowCreateModal(true); resetForm(); }}
          className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors shadow-md"
        >
          Add Entry
        </button>
      </div>

      {/* Category Rows */}
      {fetchError ? (
        <div className="py-4">
          <Alert
            variant={fetchError.code === 'NETWORK_ERROR' ? 'info' : 'error'}
            title={fetchError.code === 'NETWORK_ERROR' ? 'Connection Issue' : 'Failed to Load Entries'}
          >
            {fetchError.message || 'An unexpected error occurred.'}
          </Alert>
          <div className="mt-4 text-center">
            <Button onClick={fetchEntries}>Retry</Button>
          </div>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : sortedCategoryKeys.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">No internal knowledge entries found.</p>
          <p className="text-sm text-gray-400 mt-1">Entries are created automatically from proposal generation or manually above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedCategoryKeys.map(category => {
            const categoryEntries = groupedEntries[category];
            const isExpanded = !!expandedCategories[category];

            return (
              <div key={category} className="bg-white rounded-xl shadow-soft border border-gray-200 overflow-hidden">
                {/* Category Header Row */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <svg
                      className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="inline-flex items-center text-left px-2.5 py-1 rounded-md text-sm font-semibold bg-primary-50 text-primary-700">
                      {category.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span className="shrink-0 ml-2 whitespace-nowrap text-xs font-medium text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
                    {categoryEntries.length} {categoryEntries.length === 1 ? 'entry' : 'entries'}
                  </span>
                </button>

                {/* Expanded Entry List */}
                {isExpanded && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50/60">
                        <tr>
                          <th className="px-5 py-2 text-left text-xs font-semibold text-gray-400 uppercase pl-12">Title</th>
                          <th className="px-5 py-2 text-left text-xs font-semibold text-gray-400 uppercase">Confidence</th>
                          <th className="px-5 py-2 text-left text-xs font-semibold text-gray-400 uppercase">Workers</th>
                          <th className="px-5 py-2 text-left text-xs font-semibold text-gray-400 uppercase">Points</th>
                          <th className="px-5 py-2 text-right text-xs font-semibold text-gray-400 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {categoryEntries.map(entry => (
                          <tr key={entry.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-5 py-2.5 text-sm font-medium text-gray-900 pl-12 max-w-xs truncate">
                              {entry.title}
                            </td>
                            <td className="px-5 py-2.5">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CONFIDENCE_COLORS[entry.confidence] || 'bg-gray-100 text-gray-600'}`}>
                                {entry.confidence}
                              </span>
                            </td>
                            <td className="px-5 py-2.5">
                              <div className="flex gap-1 flex-wrap">
                                {(entry.worker_tags || []).map(tag => (
                                  <span key={tag} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">
                                    {tag.replace(/_/g, ' ')}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-5 py-2.5 text-sm text-gray-500">
                              {entry.decision_points?.length || 0}
                            </td>
                            <td className="px-5 py-2.5 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); startEdit(entry); }}
                                  title="Edit"
                                  className="p-1.5 rounded-lg text-primary hover:text-primary-600 hover:bg-primary-50 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setHistoryEntry(entry); }}
                                  title="Version history"
                                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                                  title="Deactivate"
                                  className="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingEntry) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900">
                {editingEntry ? 'Edit Internal Knowledge Entry' : 'Create Internal Knowledge Entry'}
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full rounded-lg border-gray-300 text-sm px-4 py-2.5 border focus:ring-0 focus:border-primary"
                    placeholder="e.g. jurisdiction_choices, structure_decisions"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full rounded-lg border-gray-300 text-sm px-4 py-2.5 border focus:ring-0 focus:border-primary"
                    placeholder="Decision title / summary"
                  />
                </div>

                {/* Confidence */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confidence</label>
                  <select
                    value={formData.confidence}
                    onChange={(e) => setFormData({ ...formData, confidence: e.target.value })}
                    className="w-full rounded-lg border-gray-300 text-sm px-4 py-2.5 border focus:ring-0 focus:border-primary"
                  >
                    {CONFIDENCE_OPTIONS.map(c => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                    ))}
                  </select>
                </div>

                {/* Worker Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Worker Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {WORKER_TAG_OPTIONS.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleWorkerTag(tag)}
                        className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          formData.worker_tags.includes(tag)
                            ? 'bg-blue-100 text-blue-800 border-blue-300'
                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {formData.worker_tags.includes(tag) && (
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {tag.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Decision Points */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Decision Points</label>
                  <div className="space-y-3">
                    {formData.decision_points.map((dp, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200 relative">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase">Point {index + 1}</span>
                          <button
                            onClick={() => removeDP(index)}
                            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Remove"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <label className="text-xs font-medium text-gray-600 w-28 flex-shrink-0">Decision Point</label>
                            <input
                              value={dp.point}
                              onChange={(e) => updateDP(index, 'point', e.target.value)}
                              className="flex-1 rounded-lg border-gray-300 text-sm px-3 py-2 border focus:ring-0 focus:border-primary"
                              placeholder="Decision point (required)"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="text-xs font-medium text-gray-600 w-28 flex-shrink-0">Context</label>
                            <input
                              value={dp.context}
                              onChange={(e) => updateDP(index, 'context', e.target.value)}
                              className="flex-1 rounded-lg border-gray-300 text-sm px-3 py-2 border focus:ring-0 focus:border-primary"
                              placeholder="Context"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="text-xs font-medium text-gray-600 w-28 flex-shrink-0">Rationale</label>
                            <input
                              value={dp.rationale}
                              onChange={(e) => updateDP(index, 'rationale', e.target.value)}
                              className="flex-1 rounded-lg border-gray-300 text-sm px-3 py-2 border focus:ring-0 focus:border-primary"
                              placeholder="Rationale"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="text-xs font-medium text-gray-600 w-28 flex-shrink-0">Source</label>
                            <input
                              value={dp.section_source}
                              onChange={(e) => updateDP(index, 'section_source', e.target.value)}
                              className="flex-1 rounded-lg border-gray-300 text-sm px-3 py-2 border focus:ring-0 focus:border-primary"
                              placeholder="Section source"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={addDP}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Decision Point
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => { setShowCreateModal(false); setEditingEntry(null); resetForm(); }}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={editingEntry ? handleUpdate : handleCreate}
                className="px-4 py-2 text-sm text-white bg-primary rounded-lg hover:bg-primary-600 shadow-md"
              >
                {editingEntry ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyEntry && (
        <IKEntryHistoryModal
          entryId={historyEntry.id}
          entryName={`${historyEntry.category}/${historyEntry.title}`}
          onClose={() => setHistoryEntry(null)}
          onReverted={() => fetchEntries()}
        />
      )}
    </div>
  );
};

export default BrainInternalKnowledgeTab;
